import { useMemo } from 'react';
import type { ExperimentConfig, Sample } from '../../simulation/types';
import { expandTrace, scenarioDifferences, labelPath, describeController, FF_LABELS, type RunRecord } from '../../state/runRecord';
import { useApp } from '../../state/store';
import { TimePlot, type PlotSeries, type PlotTrace } from './TimePlot';

const C = {
  target: 'var(--c-target)',
  z: 'var(--c-z)',
  ym: 'var(--c-ym)',
  v: 'var(--c-v)',
  a: 'var(--c-a)',
  e: 'var(--c-e)',
  T: 'var(--c-T)',
  Tff: 'var(--c-muted)',
  d: 'var(--c-d)',
  uP: 'var(--c-uP)',
  uI: 'var(--c-uI)',
  uD: 'var(--c-uD)',
};

export const RUN_STYLES = [
  { color: 'var(--c-run1)', dash: undefined, name: 'solid' },
  { color: 'var(--c-run2)', dash: '7 4', name: 'dashed' },
  { color: 'var(--c-run3)', dash: '2 3', name: 'dotted' },
];

interface LiveProps {
  config: ExperimentConfig;
  trace: Sample[];
  traceVersion: number;
  showAcceleration: boolean;
  showContributions: boolean;
}

export function LivePlots({ config, trace, traceVersion, showAcceleration, showContributions }: LiveProps) {
  const cursorT = useApp((s) => s.cursorT);
  const setCursor = useApp((s) => s.setCursor);
  const highlight = useApp((s) => s.highlight);
  // The trace array is mutated in place; a new wrapper per version lets memoised plots update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const traces: PlotTrace[] = useMemo(() => [{ id: 'live', samples: trace.slice() }], [trace, traceVersion]);
  const tMax = config.duration;
  const c = config.controller;
  const usesD = c.kind === 'pid';
  const common = { traces, tMax, cursorT, onCursor: setCursor, highlight };

  const altitude: PlotSeries[] = [
    { key: 'r', label: 'target $r$', color: C.target, dash: '6 4', get: (s) => s.r, highlight: 'signal:r' },
    { key: 'z', label: 'altitude $z$', color: C.z, width: 2, get: (s) => s.z, highlight: 'signal:z' },
    ...(config.noise.enabled ? [{ key: 'ym', label: 'measured', color: C.ym, dash: '1 2', width: 1, get: (s: Sample) => s.ym, highlight: 'signal:ym' as const }] : []),
  ];
  const velocity: PlotSeries[] = [
    { key: 'v', label: 'velocity $v$', color: C.v, width: 2, get: (s) => s.v, highlight: 'signal:v' },
    ...(usesD ? [{ key: 'vEst', label: 'estimate $v_{est}$', color: C.v, dash: '4 3', width: 1.2, get: (s: Sample) => s.vEst }] : []),
  ];
  const thrust: PlotSeries[] = [
    { key: 'Tcmd', label: 'requested $T_{cmd}$', color: C.T, dash: '5 3', get: (s) => s.Tcmd, highlight: 'signal:Tcmd' },
    { key: 'T', label: 'applied $T$', color: C.T, width: 2, get: (s) => s.T, highlight: 'signal:T' },
    { key: 'Tff', label: 'feedforward $T_{ff}$', color: C.Tff, dash: '1 3', get: (s: Sample) => s.Tff, highlight: 'signal:Tff' },
    ...(config.disturbance.initial !== 0 || config.disturbance.steps.length
      ? [{ key: 'd', label: 'disturbance $d$', color: C.d, dash: '8 3 2 3', get: (s: Sample) => s.d, highlight: 'force:disturbance' as const }]
      : []),
  ];
  const contributions: PlotSeries[] = [
    ...(c.kind === 'p' || c.kind === 'pi' || c.kind === 'pid' ? [{ key: 'uP', label: '$u_P$', color: C.uP, get: (s: Sample) => s.uP, highlight: 'signal:uP' as const }] : []),
    ...(c.kind === 'pi' || c.kind === 'pid' ? [{ key: 'uI', label: '$u_I$', color: C.uI, dash: '6 3', get: (s: Sample) => s.uI, highlight: 'signal:uI' as const }] : []),
    ...(usesD ? [{ key: 'uD', label: '$u_D$', color: C.uD, dash: '2 2', get: (s: Sample) => s.uD, highlight: 'signal:uD' as const }] : []),
    { key: 'ufb', label: 'total $u_{fb}$', color: 'var(--fg)', width: 1, get: (s: Sample) => s.ufb },
  ];

  return (
    <div className="plots" onPointerLeave={() => setCursor(null)}>
      <TimePlot {...common} title="Altitude" unit="m" series={altitude} height={140} digits={3} />
      <TimePlot {...common} title="Velocity" unit="m/s" series={velocity} />
      {showAcceleration && (
        <TimePlot {...common} title="Acceleration" unit="m/s²" series={[{ key: 'a', label: 'acceleration $a$', color: C.a, width: 2, get: (s) => s.a, highlight: 'signal:a' }]} />
      )}
      <TimePlot {...common} title="Error" unit="m" series={[{ key: 'e', label: 'error $e = r$ − measured', color: C.e, width: 2, get: (s) => s.e, highlight: 'signal:e' }]} refLines={[{ y: 0, label: '$e = 0$' }]} digits={3} />
      <TimePlot
        {...common}
        title="Thrust and forces"
        unit="N"
        series={thrust}
        refLines={[
          { y: config.plant.Tmax, label: `$T_{max}$ ${config.plant.Tmax} N` },
          { y: 0, label: '0 N' },
        ]}
        shade={(s) => s.sat}
        shadeLabel="saturated"
        height={140}
      />
      {showContributions && contributions.length > 1 && <TimePlot {...common} title="Controller contributions" unit="N" series={contributions} height={130} />}
    </div>
  );
}

export function ComparePlots({ runs }: { runs: RunRecord[] }) {
  const cursorT = useApp((s) => s.cursorT);
  const setCursor = useApp((s) => s.setCursor);
  const traces: PlotTrace[] = useMemo(
    () => runs.map((r, i) => ({ id: r.id, label: `Run ${r.number}`, samples: expandTrace(r.trace), dash: RUN_STYLES[i].dash, color: RUN_STYLES[i].color })),
    [runs],
  );
  // Matching axes: every run shares the same time span and value range.
  const tMax = Math.max(...runs.map((r) => r.config.duration));
  const diffs = runs.slice(1).map((r) => ({ run: r, paths: scenarioDifferences(runs[0].config, r.config) }));
  const common = { traces, tMax, cursorT, onCursor: setCursor };

  return (
    <div className="plots" onPointerLeave={() => setCursor(null)}>
      <ul className="run-legend" aria-label="Compared runs">
        {runs.map((r, i) => (
          <li key={r.id}>
            <svg width="28" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="28" y2="4" stroke={RUN_STYLES[i].color} strokeWidth={2.5} strokeDasharray={RUN_STYLES[i].dash} />
            </svg>
            <strong>Run {r.number}</strong> ({RUN_STYLES[i].name}). {describeController(r.config.controller)}. {FF_LABELS[r.config.controller.ffMode]}
          </li>
        ))}
      </ul>
      {diffs.some((d) => d.paths.length) ? (
        <div className="notice warn" role="note">
          <strong>Not a like-for-like comparison.</strong> These runs differ in the test itself:
          <ul>
            {diffs
              .filter((d) => d.paths.length)
              .map((d) => (
                <li key={d.run.id}>
                  Run {d.run.number} vs Run {runs[0].number}: {d.paths.map(labelPath).join(', ')}
                </li>
              ))}
          </ul>
        </div>
      ) : (
        runs.length > 1 && <p className="small ok-text">Same vehicle, target and disturbances in every run: differences come from the controller settings.</p>
      )}
      <TimePlot
        {...common}
        title="Altitude"
        unit="m"
        digits={3}
        height={150}
        series={[
          { key: 'z', label: 'altitude $z$', color: 'var(--fg)', width: 2, get: (s) => s.z },
          { key: 'r', label: 'target $r$', color: 'var(--c-target)', dash: '1 3', width: 1, get: (s) => s.r },
        ]}
      />
      <TimePlot {...common} title="Velocity" unit="m/s" series={[{ key: 'v', label: 'velocity $v$', color: 'var(--fg)', width: 2, get: (s) => s.v }]} />
      <TimePlot {...common} title="Error" unit="m" digits={3} series={[{ key: 'e', label: 'error $e$', color: 'var(--fg)', width: 2, get: (s) => s.e }]} refLines={[{ y: 0, label: '$e = 0$' }]} />
      <TimePlot
        {...common}
        title="Applied thrust"
        unit="N"
        series={[{ key: 'T', label: 'applied $T$', color: 'var(--fg)', width: 2, get: (s) => s.T }]}
        refLines={[{ y: Math.max(...runs.map((r) => r.config.plant.Tmax)), label: '$T_{max}$' }]}
      />
    </div>
  );
}
